import {
  AUDIO_BASE_PATH,
  BG_MUSIC_FILENAME,
  AUDIO_FADE_OUT_MS,
  BG_FADE_IN_MS,
  BG_MUSIC_KEY,
  PRIORITY_BG,
  PRIORITY_UI,
  PRIORITY_ACTION,
  PRIORITY_OUTCOME,
  BLOCK_MS_CASHOUT,
  BLOCK_MS_LOSS,
  BLOCK_MS_CRASH,
  BLOCK_MS_LAUNCH,
  BLOCK_MS_UI,
  BLOCK_MS_DEFAULT,
} from '../game/constants';

interface SoundConfigEntry {
  key: string;
  src: string;
  loop?: boolean;
  volume?: number;
  type: 'music' | 'sfx';
}

export interface AudioPreferences {
  musicEnabled: boolean;
  effectsEnabled: boolean;
}

export class AudioManager {
  private sounds: Map<string, HTMLAudioElement> = new Map();
  private musicEnabled = true;
  private effectsEnabled = true;
  private firstGesturePrimed = false;
  private originalVolumes: Map<string, number> = new Map();
  private activeBlocks: Map<number, number> = new Map();
  private soundConfig: SoundConfigEntry[] = [];

  constructor(prefs?: Partial<AudioPreferences>) {
    if (prefs) {
      if (typeof prefs.musicEnabled === 'boolean') this.musicEnabled = prefs.musicEnabled;
      if (typeof prefs.effectsEnabled === 'boolean') this.effectsEnabled = prefs.effectsEnabled;
    }

    this.initializeSounds();
    this.preload();
    this.attachGesturePrime();
  }

  private getPriority(key: string): number {
    switch (key) {
      case 'ui-open':
      case 'ui-toggle': return PRIORITY_UI;
      case 'launch':
      case 'crash': return PRIORITY_ACTION;
      case 'cashout':
      case 'loss': return PRIORITY_OUTCOME;
      case BG_MUSIC_KEY: return PRIORITY_BG;
      default: return PRIORITY_ACTION;
    }
  }

  private getBlockDurationMs(key: string): number {
    switch (key) {
      case 'cashout': return BLOCK_MS_CASHOUT;
      case 'loss': return BLOCK_MS_LOSS;
      case 'crash': return BLOCK_MS_CRASH;
      case 'launch': return BLOCK_MS_LAUNCH;
      case 'ui-open':
      case 'ui-toggle': return BLOCK_MS_UI;
      default: return BLOCK_MS_DEFAULT;
    }
  }

  private isSuppressed(priority: number, now: number): boolean {
    for (const [p, until] of this.activeBlocks) {
      if (p > priority && now < until) return true;
    }
    return false;
  }

  private applySuppression(priority: number, untilTs: number): void {
    for (let p = 0; p < priority; p++) {
      const prev = this.activeBlocks.get(p) || 0;
      if (untilTs > prev) this.activeBlocks.set(p, untilTs);
    }
  }

  private initializeSounds(): void {
    this.soundConfig = [
      { key: 'launch',    src: `${AUDIO_BASE_PATH}/sfx-launch.mp3`,    type: 'sfx',  volume: 0.2 },
      { key: 'crash',     src: `${AUDIO_BASE_PATH}/sfx-crash.mp3`,     type: 'sfx',  volume: 0.3 },
      { key: 'cashout',   src: `${AUDIO_BASE_PATH}/sfx-cashout.mp3`,   type: 'sfx',  volume: 0.95 },
      { key: 'loss',      src: `${AUDIO_BASE_PATH}/sfx-loss.mp3`,      type: 'sfx',  volume: 0.6 },
      { key: 'ui-open',   src: `${AUDIO_BASE_PATH}/sfx-ui-open.mp3`,   type: 'sfx',  volume: 0.6 },
      { key: 'ui-toggle', src: `${AUDIO_BASE_PATH}/sfx-ui-toggle.mp3`, type: 'sfx',  volume: 0.55 },
      { key: BG_MUSIC_KEY, src: `${AUDIO_BASE_PATH}/${BG_MUSIC_FILENAME}`, type: 'music', loop: true, volume: 0.55 },
    ];
  }

  private preload(): void {
    this.soundConfig.forEach(cfg => {
      const audio = new Audio(cfg.src);
      audio.loop = !!cfg.loop;
      audio.preload = 'auto';
      this.setVolumeSafe(audio, cfg.volume ?? 1);

      if (cfg.type === 'music') {
        audio.addEventListener('ended', () => {
          if (!this.musicEnabled) return;
          try { audio.currentTime = 0; audio.play().catch(() => {}); } catch {}
        });
      }

      audio.addEventListener('error', () => {
        this.sounds.delete(cfg.key);
      });

      this.originalVolumes.set(cfg.key, audio.volume);
      this.sounds.set(cfg.key, audio);
    });
  }

  private attachGesturePrime(): void {
    const prime = () => {
      if (this.firstGesturePrimed) return;
      this.firstGesturePrimed = true;
      const bg = this.sounds.get(BG_MUSIC_KEY);
      if (bg && this.musicEnabled) {
        bg.currentTime = 0;
        bg.play().catch(() => {});
      }
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
    };
    window.addEventListener('pointerdown', prime, { once: true });
    window.addEventListener('keydown', prime, { once: true });
  }

  private getConfig(key: string): SoundConfigEntry | undefined {
    return this.soundConfig.find(c => c.key === key);
  }

  setPreferences(prefs: Partial<AudioPreferences>): void {
    if (prefs.musicEnabled !== undefined) {
      this.musicEnabled = prefs.musicEnabled;
      if (!this.musicEnabled) {
        this.stop(BG_MUSIC_KEY);
      } else {
        this.startBackground();
      }
    }
    if (prefs.effectsEnabled !== undefined) {
      const turningOff = this.effectsEnabled && prefs.effectsEnabled === false;
      this.effectsEnabled = prefs.effectsEnabled;
      if (turningOff) {
        this.sounds.forEach((el, key) => {
          const cfg = this.getConfig(key);
          if (cfg && cfg.type === 'sfx' && !el.paused) {
            try { el.pause(); } catch {}
          }
        });
      }
    }
  }

  play(key: string, opts?: { allowRestart?: boolean }): void {
    const el = this.sounds.get(key);
    if (!el) return;
    const cfg = this.getConfig(key);
    if (!cfg) return;
    if (cfg.type === 'music' && !this.musicEnabled) return;
    if (cfg.type === 'sfx' && !this.effectsEnabled) return;

    const now = Date.now();
    const prio = this.getPriority(key);
    if (this.isSuppressed(prio, now)) return;

    this.sounds.forEach((otherEl, otherKey) => {
      if (otherEl && !otherEl.paused) {
        const ocfg = this.getConfig(otherKey);
        if (ocfg && ocfg.type === 'sfx' && this.getPriority(otherKey) < prio) {
          try { otherEl.pause(); } catch {}
        }
      }
    });

    if (!opts?.allowRestart && !el.paused) return;
    try {
      el.currentTime = 0;
      el.play().catch(() => {});
      const dur = this.getBlockDurationMs(key);
      this.applySuppression(prio, now + dur);
    } catch {}
  }

  stop(key: string): void {
    const el = this.sounds.get(key);
    if (!el) return;
    try { el.pause(); } catch {}
  }

  fadeOut(key: string, durationMs: number = AUDIO_FADE_OUT_MS): void {
    const el = this.sounds.get(key);
    if (!el) return;
    const startVol = el.volume;
    const start = performance.now();
    const step = (t: number) => {
      const p = Math.min(1, (t - start) / durationMs);
      this.setVolumeSafe(el, startVol * (1 - p));
      if (p < 1) requestAnimationFrame(step); else this.stop(key);
    };
    requestAnimationFrame(step);
  }

  fadeIn(key: string, target = 1, durationMs = AUDIO_FADE_OUT_MS): void {
    const el = this.sounds.get(key);
    if (!el) return;
    const cfg = this.getConfig(key);
    if (cfg) {
      if (cfg.type === 'music' && !this.musicEnabled) return;
      if (cfg.type === 'sfx' && !this.effectsEnabled) return;
    }
    this.setVolumeSafe(el, 0);
    try { el.currentTime = 0; el.play().catch(() => {}); } catch {}
    const start = performance.now();
    const baseTarget = this.originalVolumes.get(key) ?? target;
    const step = (t: number) => {
      const p = Math.min(1, (t - start) / durationMs);
      this.setVolumeSafe(el, Math.max(0, Math.min(1, baseTarget * p)));
      if (p < 1) requestAnimationFrame(step); else {
        if (cfg) {
          if (cfg.type === 'music' && !this.musicEnabled) el.pause();
          if (cfg.type === 'sfx' && !this.effectsEnabled) el.pause();
        }
      }
    };
    requestAnimationFrame(step);
  }

  startBackground(): void {
    if (!this.musicEnabled) return;
    this.fadeIn(BG_MUSIC_KEY, 1, BG_FADE_IN_MS);
  }

  stopBackground(): void {
    this.stop(BG_MUSIC_KEY);
  }

  private setVolumeSafe(el: HTMLAudioElement, v: number): void {
    try { el.volume = Math.max(0, Math.min(1, v)); } catch {}
  }
}
